class ShopTheLook extends HTMLElement {
  constructor() {
    super();
    this.currentSetIndex = 0;
    this.currentActiveProductIndex = '0';
    this.totalSets = 0;
    this.slides = [];
    this.productSets = [];
    this.indicators = [];
    this.arrows = {};
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.init();

      if (this.slides.length === 0) {
        setTimeout(() => {
          this.init();
        }, 100);
      }
    });
  }

  init() {
    this.totalSets = parseInt(this.getAttribute('data-total-sets')) || 0;
    
    const elementsFound = this.findElements();
    if (!elementsFound) return;
    
    this.bindEvents();
    this.setInitialActive();
  }

  findElements() {
    this.slides = Array.from(this.querySelectorAll('.shop-the-look__slide'));
    this.productSets = Array.from(this.querySelectorAll('.shop-the-look__products-set'));
    this.indicators = Array.from(this.querySelectorAll('.shop-the-look__indicator'));
    
    this.arrows.prev = this.querySelector('[is="prev-button"]');
    this.arrows.next = this.querySelector('[is="next-button"]');
    
    if (this.slides.length === 0 || this.productSets.length === 0) {
      console.warn('ShopTheLook: No slides or product sets found', {
        slides: this.slides.length,
        productSets: this.productSets.length,
        totalSets: this.totalSets
      });
      return false;
    }
    return true;
  }

  bindEvents() {
    if (this.arrows.prev) {
      this.arrows.prev.addEventListener('click', () => this.prevSet());
    }
    if (this.arrows.next) {
      this.arrows.next.addEventListener('click', () => this.nextSet());
    }

    this.indicators.forEach(indicator => {
      indicator.addEventListener('click', (e) => {
        const setIndex = parseInt(e.currentTarget.getAttribute('data-set-index'));
        this.goToSet(setIndex);
      });
    });

    this.bindProductInteractions();
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  bindProductInteractions() {
    const dots = this.querySelectorAll('.shop-the-look__dot[data-product-index]');
    const cards = this.querySelectorAll('.shop-the-look__card[data-product-index]');

    dots.forEach(dot => {
      dot.addEventListener('click', this.handleDotClick.bind(this));
    });

    cards.forEach(card => {
      card.addEventListener('click', this.handleCardClick.bind(this));
    });
  }

  handleDotClick(event) {
    event.preventDefault();
    const productIndex = event.currentTarget.getAttribute('data-product-index');
    const setIndex = parseInt(event.currentTarget.getAttribute('data-set-index'));
    
    if (setIndex !== this.currentSetIndex) {
      this.goToSet(setIndex);
    }
    
    this.setActiveProduct(productIndex, setIndex);
  }

  handleCardClick(event) {
    event.preventDefault();
    const productIndex = event.currentTarget.getAttribute('data-product-index');
    const setIndex = parseInt(event.currentTarget.getAttribute('data-set-index'));
    
    if (setIndex === this.currentSetIndex) {
      this.setActiveProduct(productIndex, setIndex);
    }
  }

  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.prevSet();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextSet();
        break;
    }
  }

  setActiveProduct(productIndex, setIndex = this.currentSetIndex) {
    if (setIndex !== this.currentSetIndex) return;
    if (this.currentActiveProductIndex === productIndex) return;
    
    this.currentActiveProductIndex = productIndex;
    this.removeActiveClasses(setIndex);
    
    const targetDot = this.querySelector(
      `.shop-the-look__dot[data-product-index="${productIndex}"][data-set-index="${setIndex}"]`
    );
    const targetCard = this.querySelector(
      `.shop-the-look__card[data-product-index="${productIndex}"][data-set-index="${setIndex}"]`
    );

    if (targetDot) {
      targetDot.classList.add('active');
      targetDot.setAttribute('aria-current', 'true');
    }
    
    if (targetCard) {
      targetCard.classList.add('active');
    }

    this.dispatchEvent(new CustomEvent('shop-the-look:product-changed', {
      detail: { 
        productIndex, 
        setIndex, 
        dot: targetDot, 
        card: targetCard 
      },
      bubbles: true
    }));
  }

  removeActiveClasses(setIndex) {
    const dots = this.querySelectorAll(`.shop-the-look__dot[data-set-index="${setIndex}"]`);
    dots.forEach(dot => {
      dot.classList.remove('active');
      dot.setAttribute('aria-current', 'false');
    });

    const cards = this.querySelectorAll(`.shop-the-look__card[data-set-index="${setIndex}"]`);
    cards.forEach(card => {
      card.classList.remove('active');
    });
  }

  goToSet(setIndex) {
    if (setIndex === this.currentSetIndex || setIndex < 0 || setIndex >= this.totalSets) return;
    
    const prevSetIndex = this.currentSetIndex;
    this.currentSetIndex = setIndex;
    
    if (this.slides[prevSetIndex]) {
      this.slides[prevSetIndex].classList.remove('active');
    }
    if (this.productSets[prevSetIndex]) {
      this.productSets[prevSetIndex].classList.remove('active');
    }
    if (this.indicators[prevSetIndex]) {
      this.indicators[prevSetIndex].classList.remove('active');
    }
    
    if (this.slides[setIndex]) {
      this.slides[setIndex].classList.add('active');
    }
    if (this.productSets[setIndex]) {
      this.productSets[setIndex].classList.add('active');
    }
    if (this.indicators[setIndex]) {
      this.indicators[setIndex].classList.add('active');
    }
    
    this.currentActiveProductIndex = null;
    this.setActiveProduct('0', setIndex);
    
    this.updateArrowStates();
    
    this.dispatchEvent(new CustomEvent('shop-the-look:set-changed', {
      detail: { 
        currentSetIndex: setIndex, 
        previousSetIndex: prevSetIndex 
      },
      bubbles: true
    }));
  }

  nextSet() {
    this.goToSet(this.currentSetIndex + 1);
  }

  prevSet() {
    this.goToSet(this.currentSetIndex - 1);
  }

  updateArrowStates() {
    if (this.arrows.prev) {
      this.arrows.prev.disabled = this.currentSetIndex === 0;
      this.arrows.prev.setAttribute('aria-disabled', this.currentSetIndex === 0);
    }
    if (this.arrows.next) {
      this.arrows.next.disabled = this.currentSetIndex === this.totalSets - 1;
      this.arrows.next.setAttribute('aria-disabled', this.currentSetIndex === this.totalSets - 1);
    }
  }

  setInitialActive() {
    if (this.slides.length > 0 && this.productSets.length > 0) {
      this.currentSetIndex = 0;
      this.currentActiveProductIndex = null;
      this.setActiveProduct('0', 0);
      this.updateArrowStates();
    }
  }

  getCurrentSet() {
    return this.currentSetIndex;
  }

  getCurrentProduct() {
    return this.currentActiveProductIndex;
  }

  setActiveByIndex(setIndex, productIndex = '0') {
    this.goToSet(setIndex);
    this.setActiveProduct(productIndex, setIndex);
  }

  refresh() {
    this.init();
  }
}

if (customElements && !customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
